import {
  getEventHistoryInteractionId,
  isEventHistoryPath,
} from "@/utils/awsRUM/eventHistoryPrivacy";

type AWSRegion = "us-east-1" | "us-west-2" | "eu-west-1";
type AWSRoleArn = `arn:aws:iam::${number}:role/${string}`;
type AWSIdentityPoolId = `${string}:${string}`;

interface TelemetryEvent {
  event: string;
  element: typeof document | typeof window;
}

interface RUMConfig {
  config: {
    sessionSampleRate: number;
    pageSampleRate?: number;
    guestRoleArn: AWSRoleArn;
    identityPoolId: AWSIdentityPoolId;
    endpoint: `https://dataplane.rum.${AWSRegion}.amazonaws.com`;
    telemetries: unknown[];
    allowCookies: boolean;
    enableXRay: boolean;
  };
  APPLICATION_ID: string;
  APPLICATION_VERSION: string;
  APPLICATION_REGION: AWSRegion;
}

interface EnvConfig {
  [region: string]: {
    [env: string]: RUMConfig;
  };
}

export const getInteractionId = (event: Event): string => {
  const target = event.target as HTMLElement;
  if (!target) return "";
  if (isEventHistoryPath(window.location.pathname)) {
    return getEventHistoryInteractionId(target);
  }

  const MAX_LENGTH = 500;
  const MAX_INNER_TEXT_LENGTH = 100;

  const getNestedContent = (element: HTMLElement): string => {
    const children = Array.from(element.children);
    if (children.length === 0) {
      return element.innerText?.trim().slice(0, MAX_INNER_TEXT_LENGTH) || "";
    }

    return children
      .map((child) => {
        const childElement = child as HTMLElement;
        const childAttributes = Array.from(childElement.attributes)
          .map((attr) => `${attr.name}="${attr.value}"`)
          .join(" ");

        const childContent = getNestedContent(childElement);
        const tagName = childElement.tagName.toLowerCase();

        return `<${tagName}${childAttributes ? " " + childAttributes : ""}>${childContent}</${tagName}>`;
      })
      .join("");
  };

  const attributes = Array.from(target.attributes)
    .map((attr) => `${attr.name}="${attr.value}"`)
    .join(" ");

  const tagName = target.tagName.toLowerCase();
  const content = getNestedContent(target);

  let fullContent = `<${tagName}${attributes ? " " + attributes : ""}>${content}`;

  // Special handling for self-closing tags
  if (["img", "br", "hr", "input"].includes(tagName)) {
    fullContent = fullContent.replace(/>$/, "/>");
  } else {
    fullContent += `</${tagName}>`;
  }

  return fullContent.length > MAX_LENGTH ? `${fullContent.slice(0, MAX_LENGTH)}...` : fullContent;
};

const telemetryEvents: TelemetryEvent[] = [
  { event: "click", element: document },
  { event: "submit", element: document },
  { event: "navigation", element: document },
  { event: "touchstart", element: document },
];

const baseConfig = {
  sessionSampleRate: 1,
  sessionEventLimit: 0,
  batchLimit: 500,
  sessionLengthSeconds: 3600,
  allowCookies: true,
  enableXRay: true,
  telemetries: [
    ["errors", { stackTraceLength: 1000 }],
    "performance",
    ["http", { recordAllRequests: true }],
    "session",
    "dom",
    [
      "interaction",
      {
        events: telemetryEvents,
        interactionId: getInteractionId,
        enableMutationObserver: true,
      },
    ],
    [
      "resources",
      {
        recordResourceUrl: true,
        recordResourceType: true,
      },
    ],
    [
      "custom",
      {
        allowedEvents: ["*"],
      },
    ],
  ],
};

const envConfig: EnvConfig = {
  USAmazon: {
    test: {
      config: {
        ...baseConfig,
        guestRoleArn:
          "arn:aws:iam::012619892873:role/beta-us-west-2-YTYard360RUM-UnauthenticatedRole",
        identityPoolId: "us-west-2:d9c3a0f5-e878-44ce-9534-1072d9d39db8",
        endpoint: "https://dataplane.rum.us-west-2.amazonaws.com",
      },
      APPLICATION_ID: "28816a25-afaa-41b5-a783-227152b4d3c9",
      APPLICATION_VERSION: "1.0.0",
      APPLICATION_REGION: "us-west-2",
    },
    master: {
      config: {
        ...baseConfig,
        pageSampleRate: 1,
        guestRoleArn:
          "arn:aws:iam::104941723580:role/gamma-us-east-1-YTYard360RUM-UnauthenticatedRole",
        identityPoolId: "us-east-1:cab8bca3-2ab1-4d22-9572-79638d0e088b",
        endpoint: "https://dataplane.rum.us-east-1.amazonaws.com",
      },
      APPLICATION_ID: "2f4ef656-ffd1-4e14-b735-8f8eea069ddb",
      APPLICATION_VERSION: "1.0.0",
      APPLICATION_REGION: "us-east-1",
    },
    prod: {
      config: {
        ...baseConfig,
        guestRoleArn:
          "arn:aws:iam::817745903795:role/prod-IAD-us-east-1-YTYard360RUM-UnauthenticatedRole",
        identityPoolId: "us-east-1:3b0efdad-9aaf-40ad-8274-e98061fd833d",
        endpoint: "https://dataplane.rum.us-east-1.amazonaws.com",
      },
      APPLICATION_ID: "af7f34b4-20e2-4f55-8b97-7e43a9333ba4",
      APPLICATION_VERSION: "1.0.0",
      APPLICATION_REGION: "us-east-1",
    },
  },
  EUAmazon: {
    master: {
      config: {
        ...baseConfig,
        guestRoleArn:
          "arn:aws:iam::583167906088:role/gamma-eu-west-1-YTYard360RUM-UnauthenticatedRole",
        identityPoolId: "eu-west-1:b6c5cf0a-0864-498d-b10d-33c0430b8470",
        endpoint: "https://dataplane.rum.eu-west-1.amazonaws.com",
      },
      APPLICATION_ID: "a899f2e3-bc4b-46b3-a531-d5dbf5f452a0",
      APPLICATION_VERSION: "1.0.0",
      APPLICATION_REGION: "eu-west-1",
    },
    prod: {
      config: {
        ...baseConfig,
        guestRoleArn:
          "arn:aws:iam::773934869068:role/prod-DUB-eu-west-1-YTYard360RUM-UnauthenticatedRole",
        identityPoolId: "eu-west-1:ef99a0a9-6a84-4a6d-ba5a-b2b59f3cea06",
        endpoint: "https://dataplane.rum.eu-west-1.amazonaws.com",
      },
      APPLICATION_ID: "99384f09-b028-44a6-bdee-b75e91396b2a",
      APPLICATION_VERSION: "1.0.0",
      APPLICATION_REGION: "eu-west-1",
    },
  },
  JPAmazon: {
    master: {
      config: {
        ...baseConfig,
        guestRoleArn:
          "arn:aws:iam::223555751224:role/gamma-us-west-2-YTYard360RUM-UnauthenticatedRole",
        identityPoolId: "us-west-2:1bcf0a01-b020-46af-81f5-efed42ddec58",
        endpoint: "https://dataplane.rum.us-west-2.amazonaws.com",
      },
      APPLICATION_ID: "4dcc272a-446e-44d6-8cfb-35b232e42990",
      APPLICATION_VERSION: "1.0.0",
      APPLICATION_REGION: "us-west-2",
    },
    prod: {
      config: {
        ...baseConfig,
        guestRoleArn:
          "arn:aws:iam::026191072096:role/prod-PDX-us-west-2-YTYard360RUM-UnauthenticatedRole",
        identityPoolId: "us-west-2:04950cca-3be1-46e8-9828-4f9017191083",
        endpoint: "https://dataplane.rum.us-west-2.amazonaws.com",
      },
      APPLICATION_ID: "c3aecca3-751c-4f44-ade2-fffe8a4411bd",
      APPLICATION_VERSION: "1.0.0",
      APPLICATION_REGION: "us-west-2",
    },
  },
};

export default envConfig;
